import { Button, CheckBox, Popup } from 'devextreme-react';
import Form, { GroupItem } from 'devextreme-react/form';
import React, { FormEvent, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { postApi } from '../../../apiUtils';
import ReportFilterSelector, {
  FilterActionState,
} from '../../../components/reportSelector/ReportSelector';
import { useAssetClass } from '../../../contexts/assetClass';
import { useLoading } from '../../../contexts/loadingContext';
import { useTranslation } from '../../../contexts/translation';
import { clear, getParameters, parseParameters, useField } from '../../../hooks/useField';
import { useSessionStorage } from '../../../hooks/useSessionStorage';
import { Asset } from '../../../types/asset.types';
import { AssetLocation } from '../../../types/assetLocation.types';
import { AssetStatus } from '../../../types/assetStatus.types';
import { AssetType } from '../../../types/assetType.type';
import { FieldType } from '../../../types/field.types';
import { Rule } from '../../../types/rule.types';
import { API_URL, fetcher } from '../../../utils/apiUrl';
import { STORAGE_KEYS } from '../../../utils/localStorageKeys';
import FieldLabel from '../components/FieldLabel';
import FormField from '../components/FormField';
import DeleteButton from '../components/ReportDeleteButton';
import '../ReportFilter.scss';

type Props = {
  dataGridRef: React.Ref<any>;
  onDataChange: (data: Array<Asset> | null) => void;
  onFilterChange: (filter: string | null) => void;
  report: {
    id: number;
    key: string;
  };
  filterVisibility: boolean;
  loadDefaultSetting: boolean;
};

/**
 * Komponenta představující filtr s report selectorem pro Report strojů (Asset)
 */
export default function AssetReportFilter({
  dataGridRef,
  onDataChange,
  onFilterChange,
  report,
  filterVisibility,
  loadDefaultSetting,
}: Props) {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const [dialogAction, setDialogAction] = useState<FilterActionState | null>(null);
  const [isDefault, setDefault] = useState<boolean>(false);
  const { startLoading, loading, stopLoading } = useLoading();
  const { assetClass } = useAssetClass();

  const { translate } = useTranslation();
  const transFormName = 'Asset.List';
  const transFormRepSelector = 'Reports.ReportSelector';
  const transFormReportBar = 'Reports.Bar';
  // reference pro report selector na kterou jsou navázané funkce pro práci s filtry:
  //  - smazání, přidání / editace filtru
  const selectorRef = useRef<any>();

  const [filterSettingSession, setFilterSettingSession] = useSessionStorage(
    STORAGE_KEYS.ASSETS_REPORT_FILTER_DESKTOP,
    {},
  );

  /**
   * Funkce pro fetch dat z API dle parametrů ve fieldech / defaultních hodnot
   */
  const getData = async (params = {}) => {
    const response = await postApi<Array<Asset>>({
      url: API_URL.ASSET_REPORT_REQUEST,
      data: params,
      hideNotifications: true,
    });
    onDataChange(response ?? null);
    stopLoading();
  };

  useEffect(() => {
    const params = {
      locationIdArr: null,
      statusIdArr: null,
      typeIdArr: null,
      active: true,
    };
    getData(params);
  }, []);

  /**
   * Funkce pro vyhledávání - po zadání hodnot ve filter baru
   */
  const onSearch = (e: FormEvent) => {
    startLoading();
    e.preventDefault();
    const params = parseParameters(fields);
    getData(params);
    const filterParams = getParameters(fields);
    setFilterSettingSession(filterParams);
  };

  /**
   * Funkce pro smazání hodnot z fieldů - reset funkce
   */
  const clearDataAndFields = () => {
    clear(fields);
    onDataChange([]);
  };

  /**
   * SWR fetch dat z API pro naplnění field data sourců
   */

  // Číselník typů
  const { data: types } = useSWR<AssetType[]>(
    `${API_URL.ASSET_TYPE_LIST_BY_CLASS}?classId=${assetClass}&visibleOnly=true`,
    fetcher,
  );

  // Číselník stavů
  const { data: states } = useSWR<AssetStatus[]>(`${API_URL.ASSET_STATUS_LIST}`, fetcher);

  // Číselník lokací
  const { data: locations } = useSWR<AssetLocation[]>(`${API_URL.ASSET_LOCATION_LIST}`, fetcher);

  /**
   * Definice fieldů pro filter bar + form na přidání nového / editaci stávajího filtru
   */

  const filterNameField = useField({
    name: 'name',
    fieldType: FieldType.Text,
    placeholder: translate!('Filter name', transFormRepSelector),
    rules: [{ type: Rule.RequiredRule, message: 'Filter name is required' }],
    maxLength: 50,
  });

  const locationIdArrayField = useField({
    name: 'locationIdArr',
    fieldType: FieldType.MultipleOptions,
    dataSource: locations,
    keyExpr: 'id',
    displayExpr: 'name',
    placeholder: translate!('Asset location', transFormName),
  });

  const statusIdArrayField = useField({
    name: 'statusIdArr',
    fieldType: FieldType.MultipleOptions,
    dataSource: states,
    keyExpr: 'id',
    displayExpr: 'name',
    placeholder: translate!('Status', transFormName),
  });

  const typeIdArrayField = useField({
    name: 'typeIdArr',
    fieldType: FieldType.MultipleOptions,
    dataSource: types,
    keyExpr: 'id',
    displayExpr: 'name',
    placeholder: translate!('Type', transFormName),
  });

  const isActiveField = useField({
    name: 'active',
    fieldType: FieldType.SingleOptions,
    dataSource: [true, false],
    // keyExpr: 'id',
    // displayExpr: 'name',
    placeholder: translate!('Active', transFormName),
  });

  const fields = [filterNameField, locationIdArrayField, statusIdArrayField, typeIdArrayField];

  /**
   * Funkce pro zpracování formuláře při přidání nového / editaci stávajícího filtru
   */
  const onFilterDialogSubmit = (e) => {
    e.preventDefault();
    selectorRef.current.saveOrEditFilter(dialogAction);
  };

  const handleDialogVisibility = (action: FilterActionState | null = null) => {
    setDialogAction(action);
    setIsModalVisible(!!action);
  };

  return (
    <>
      <div>
        <div
          className="report-selector-bar"
          style={{ display: filterVisibility ? 'block' : 'none' }}
        >
          <ReportFilterSelector
            ref={selectorRef}
            filterName={filterNameField.value}
            fields={fields}
            dataGridRef={dataGridRef}
            onClear={clearDataAndFields}
            onFilterChange={onFilterChange}
            report={report}
            openDialog={handleDialogVisibility}
            fetchData={getData}
            isDefault={isDefault}
            setIsDefault={(value) => setDefault(value)}
            loadDefaultSetting={loadDefaultSetting}
            loadDefaultSettingStorageKey={STORAGE_KEYS.ASSETS_REPORT_FILTER_DESKTOP}
          />
        </div>
        {isModalVisible && (
          <Popup
            visible={isModalVisible}
            dragEnabled={false}
            closeOnOutsideClick={false}
            onHiding={() => handleDialogVisibility(null)}
            showTitle={true}
            title={
              dialogAction === FilterActionState.EDIT
                ? translate!('Edit selected filter', transFormRepSelector)
                : translate!('Save a new filter', transFormRepSelector)
            }
            container=".dx-viewport"
            width="auto"
            height="auto"
          >
            {/* filter add/edit dialog */}
            <form onSubmit={onFilterDialogSubmit}>
              <Form colCount={3} className="fields-container">
                <GroupItem colSpan={3} colCount={3}>
                  <GroupItem>
                    <FieldLabel label={filterNameField.placeholder} />
                    <FormField field={filterNameField} />
                  </GroupItem>
                </GroupItem>
                <GroupItem colSpan={3} colCount={3}>
                  <GroupItem>
                    <FieldLabel label={locationIdArrayField.placeholder} />
                    <FormField field={locationIdArrayField} alt />
                  </GroupItem>
                  <GroupItem>
                    <FieldLabel label={statusIdArrayField.placeholder} />
                    <FormField field={statusIdArrayField} alt />
                  </GroupItem>
                  <GroupItem>
                    <FieldLabel label={typeIdArrayField.placeholder} />
                    <FormField field={typeIdArrayField} alt />
                  </GroupItem>
                  <GroupItem>
                    <CheckBox value={isDefault} onValueChanged={(e) => setDefault(e.value)} />{' '}
                    {translate!('Is default', transFormRepSelector)}
                  </GroupItem>
                </GroupItem>
                <GroupItem colSpan={3} colCount={3} cssClass={'action-buttons-view'}>
                  {dialogAction === FilterActionState.EDIT && <DeleteButton ref={selectorRef} />}
                  <Button
                    className="button-spacing"
                    text={translate!('Save', transFormRepSelector)}
                    icon="save"
                    type="success"
                    useSubmitBehavior
                    disabled={loading}
                  />
                </GroupItem>
              </Form>
            </form>
          </Popup>
        )}
      </div>
      <form onSubmit={onSearch} style={{ display: filterVisibility ? 'block' : 'none' }}>
        <Form colCount={7} className="popup-container" style={{ justify: 'center' }}>
          <GroupItem>
            <FieldLabel label={locationIdArrayField.placeholder} />
            <FormField field={locationIdArrayField} />
          </GroupItem>
          <GroupItem>
            <FieldLabel label={statusIdArrayField.placeholder} />
            <FormField field={statusIdArrayField} />
          </GroupItem>
          <GroupItem>
            <FieldLabel label={typeIdArrayField.placeholder} />
            <FormField field={typeIdArrayField} />
          </GroupItem>
          <GroupItem cssClass={'action-buttons-view'}>
            <Button
              className="button-spacing"
              type="success"
              text={translate!('Search', transFormReportBar)}
              useSubmitBehavior
              disabled={loading}
            />
          </GroupItem>
        </Form>
      </form>
    </>
  );
}
